import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";

import { useCoinBalance } from "@/lib/coin/coin.store";
import { useSubscription } from "@/lib/subscription/useSubscription.hook";
import { AnalyticsService } from "@/lib/analytics/analytics.service";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";

function formatHM(
	resetAt: string | null,
): { hours: number; minutes: number } | null {
	if (!resetAt) return null;
	const diff = new Date(resetAt).getTime() - Date.now();
	if (Number.isNaN(diff)) return null;
	const totalMinutes = Math.max(0, Math.ceil(diff / 60000));
	return {
		hours: Math.floor(totalMinutes / 60),
		minutes: totalMinutes % 60,
	};
}

/**
 * Small indicator rendered near the coin balance for TRIALING users only.
 * Reads the same coin store the sidebar balance uses, so a spend that
 * refreshes coins also updates the sprint without a reload.
 */
export function TrialCoinWindowIndicator({
	className,
}: {
	className?: string;
}) {
	const { t } = useTranslation();
	const { subscriptionStatus } = useSubscription();
	const { trialWindow } = useCoinBalance();
	const isTrialing = subscriptionStatus === "TRIALING";

	useEffect(() => {
		if (!trialWindow) return;
		const ratio = trialWindow.used / Math.max(1, trialWindow.limit);
		if (ratio >= 0.75 && ratio < 1) {
			const sessionKey = "trial_cap_warning_shown";
			if (!sessionStorage.getItem(sessionKey)) {
				sessionStorage.setItem(sessionKey, "1");
				AnalyticsService.track("trial_cap_warning_shown", {
					used: trialWindow.used,
					limit: trialWindow.limit,
				});
			}
		}
	}, [trialWindow]);

	if (!isTrialing || !trialWindow) return null;

	const hm = formatHM(trialWindow.windowResetAt);

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<div
					className={[
						"inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/5 px-2.5 py-1 text-[11px] font-medium text-emerald-300 cursor-default",
						className ?? "",
					].join(" ")}
				>
					<Sparkles className="h-3 w-3" />
					<span>
						{t("trial.window.label", {
							used: trialWindow.used,
							limit: trialWindow.limit,
						})}
					</span>
					{hm && (hm.hours > 0 || hm.minutes > 0) && (
						<span className="text-emerald-400/80">
							•{" "}
							{t("trial.window.resets", {
								hours: hm.hours,
								minutes: hm.minutes,
							})}
						</span>
					)}
				</div>
			</TooltipTrigger>
			<TooltipContent side="bottom" className="max-w-xs text-balance">
				{t("trial.window.tooltip", { limit: trialWindow.limit })}
			</TooltipContent>
		</Tooltip>
	);
}
